import { ACTIONS } from '../Constants';
import { assignTeamDetails } from '../utils/CollectionUtils';
import { buildQueryString } from '../utils/StringUtils';
import { VerbFetch } from '../VerbFetch';
import { cloneDeep } from 'lodash';

/**
 *
 * @param {Object} params
 * @param {String} actionType
 * @return {function(*)}
 * @private
 */
const _getManyCollections = (params, actionType) => {
    const { pathsContext, ...rest } = params;
    const baseUrl = `${pathsContext.serverUrl}/collections`;
    let url;

    if (rest && rest.dispatchDisabled) {
        delete rest.dispatchDisabled;
        url = buildQueryString(rest, baseUrl);
        return VerbFetch(url);
    }

    url = buildQueryString(rest, baseUrl);
    return (dispatch, getState) => {
        dispatch({
            type: ACTIONS.GET_MANY_COLLECTIONS_START
        });
        return VerbFetch(url).then((body) => {
            const { teams } = getState();
            const newCollections = assignTeamDetails(body, teams);
            dispatch({
                type: actionType,
                payload: {
                    collections: newCollections
                }
            });
            return newCollections;
        });
    };
};

/**
 *
 * @param {Object} query
 * @return {function(*)}
 */
export const getFilteredCollections = (query) => _getManyCollections(query, ACTIONS.GET_FILTERED_COLLECTIONS);

/**
 *
 * @param {Object} params
 * @return {function(*)}
 */
export const getManyCollections = (params) => _getManyCollections(params, ACTIONS.GET_MANY_COLLECTIONS);

export const getCollection = (params) => {
    const { collectionId, dispatchDisabled, getFullProjection = false, pathsContext } = params;
    const url = `${pathsContext.serverUrl}/collections/${collectionId}?getFullProjection=${getFullProjection}`;

    if (dispatchDisabled) {
        return VerbFetch(url);
    }
    return (dispatch, getState) =>
        VerbFetch(url).then((body) => {
            const { teams } = getState();
            const [newCollection] = assignTeamDetails([body], teams);

            dispatch({
                type: ACTIONS.GET_COLLECTION,
                payload: {
                    collection: newCollection
                }
            });
            return body;
        });
};

export const getRecommendedAndAssignedCollections = (params) => {
    const { pathsContext } = params;
    const baseUrl = `${pathsContext.serverUrl}/collections/recommended`;

    return (dispatch) => {
        VerbFetch(baseUrl).then((body) => {
            dispatch({
                type: ACTIONS.GET_RECOMMENDED_AND_ASSIGNED_COLLECTIONS,
                payload: {
                    collections: body
                }
            });
            return body;
        });
    };
};

export const getUserAccessibleCollectionsForExplore = (params) => {
    const { pathsContext } = params;
    return VerbFetch(`${pathsContext.serverUrl}/collections/userexploresearch`, {
        method: 'GET'
    })
        .then((result) => result)
        .catch((err) => {
            console.error(err);
        });
};

export const getInitialCollectionsForExplore = (params) => {
    const { pathsContext, ...rest } = params;
    const baseUrl = `${pathsContext.serverUrl}/collections/exploresearch`;
    const url = buildQueryString(rest, baseUrl);

    return (dispatch) =>
      VerbFetch(url, {
          method: 'GET'
      })
          .then((result) => {
            dispatch({
              type: ACTIONS.GET_MANY_COLLECTIONS,
              payload: {
                collections: result.collections
              }
          });
          return result;
          })
          .catch((err) => {
              console.error(err);
          });
};

export const postCollection = (params) => {
    const { body, pathsContext } = params;
    return (dispatch) => {
        dispatch({
            type: ACTIONS.POST_COLLECTION_START,
            payload: {
                body
            }
        });
        return VerbFetch(`${pathsContext.adminServerUrl}/collections`, {
            method: 'POST',
            body
        }).then((body) => {
            dispatch({
                type: ACTIONS.POST_COLLECTION_SUCCESS,
                payload: {
                    collection: body
                }
            });
            return body;
        });
    };
};

export const putCollection = (params) => {
    const { collectionId, body, options, pathsContext } = params;
    const baseUrl = `${pathsContext.adminServerUrl}/collections/${collectionId}`;
    const url = buildQueryString(options, baseUrl);
    return (dispatch) => {
        dispatch({
            type: ACTIONS.PUT_COLLECTION_START,
            payload: {
                body
            }
        });
        return VerbFetch(url, {
            method: 'PUT',
            body
        }).then((body) => {
            dispatch({
                type: ACTIONS.PUT_COLLECTION_SUCCESS,
                payload: {
                    collection: body
                }
            });
            return body;
        });
    };
};

export const archiveCollection = (params) => {
    const { body, pathsContext } = params;
    return (dispatch) => {
        dispatch({
            type: ACTIONS.PUT_COLLECTION_START,
            payload: {
                body
            }
        });
        return VerbFetch(`${pathsContext.adminServerUrl}/collections/${body._id}/archive`, {
            method: 'PUT',
            body: { isArchived: true }
        }).then((body) => {
            dispatch({
                type: ACTIONS.PUT_ARCHIVED_COLLECTION_SUCCESS,
                payload: {
                    collection: body
                }
            });
            return body;
        });
    };
};

export const unarchiveCollection = (params) => {
    const { body, pathsContext } = params;
    return (dispatch) => {
        dispatch({
            type: ACTIONS.PUT_COLLECTION_START,
            payload: {
                body
            }
        });
        return VerbFetch(`${pathsContext.adminServerUrl}/collections/${body._id}/archive`, {
            method: 'PUT',
            body: { isArchived: false }
        }).then((body) => {
            dispatch({
                type: ACTIONS.PUT_UNARCHIVED_COLLECTION_SUCCESS,
                payload: {
                    collection: body
                }
            });
            return body;
        });
    };
};

export const getArchivedCollections = (params) => {
    const { pathsContext, ...rest } = params;
    const baseUrl = `${pathsContext.adminServerUrl}/collections/archived`;
    const url = buildQueryString(rest, baseUrl);

    return (dispatch) =>
        VerbFetch(url).then((body) => {
            dispatch({
                type: ACTIONS.GET_MANY_ARCHIVED_COLLECTIONS,
                payload: {
                    collections: body
                }
            });
            return body;
        });
};

export const getCollectionStatus = (params) => {
    const { collectionId, pathsContext } = params;
    return VerbFetch(`${pathsContext.adminServerUrl}/collections/${collectionId}/status`, {
        method: 'GET'
    })
        .then((result) => result)
        .catch((err) => {
            console.error(err);
            return { error: true };
        });
};

export const pushCollectionToCompanyMembers = (params) => {
    const { body, pathsContext } = params;
    const baseUrl = `${pathsContext.adminServerUrl}/usercollections/push`;
    return VerbFetch(baseUrl, {
        method: 'POST',
        body
    })
        .then((result) => result)
        .catch((err) => {
            console.error(err);
            return { status: 'Something went wrong', error: true };
        });
};

export const putCollectionAssociatedDimensions = (params) => {
    const { body, collectionId, pathsContext } = params;
    const baseUrl = `${pathsContext.adminServerUrl}/collections/${collectionId}/dimensions`;
    return VerbFetch(baseUrl, {
        method: 'PUT',
        body
    })
        .then((result) => result)
        .catch((err) => {
            console.error(err);
            return { status: 'Something went wrong', error: true };
        });
};

export const putCollectionAssociatedCompanyLicenses = (params) => {
    const { body, collectionId, pathsContext } = params;
    const baseUrl = `${pathsContext.adminServerUrl}/collections/${collectionId}/licenses`;
    return VerbFetch(baseUrl, {
        method: 'PUT',
        body
    }).then((result) => result);
};

export const clearSearchedCollections = () => (dispatch) =>
    dispatch({
        type: ACTIONS.CLEAR_SEARCHED_COLLECTIONS
    });
